// ─────────────────────────────────────────────────────────────────
// Phase B.2 — Affichage lecture seule du plan d'analyses
// Date : 2026-05-09
//
// Bloc lecture seule a inclure dans la fiche cliente (NutritionConsultation).
// Charge le dernier analysis_plan pour le client_id depuis Supabase.
// Affiche : status, date, tests selectionnes, cout, marge, notes IA.
//
// Pas de modification inline (V4 = simple d'abord). L'edition viendra
// si le besoin est observe sur les pilotes.
// ─────────────────────────────────────────────────────────────────

import { useEffect, useState } from 'react';
import { supabase } from './supabaseClient';
import { PACK_DEFINITIONS } from './services/packSystem';
import { formatChf } from './services/formatChf';
import LabBadge from './components/LabBadge';
import ControleDemandeMgd from './components/ControleDemandeMgd';
import { alignerPlan, messageRefus } from './services/alignerPlan';
import ConfirmationBonsOrtho from './components/ConfirmationBonsOrtho';
import EnvoyerAFacturer from './components/EnvoyerAFacturer';
import { controlesManquants, titreRappelControle } from './services/demandeMgd';

const STATUS_LABELS = {
  draft: { label: 'Brouillon', color: '#888' },
  sent: { label: 'Envoye a la cliente', color: '#2d5a3d' },
  in_progress: { label: 'En cours', color: '#856404' },
  completed: { label: 'Termine', color: '#1a4028' },
};

// 25.08.2026 — NEXT_STATUS et advanceStatus ont ete retires avec le bouton
// de transition. Le parcours est seul maitre de l'avancement ; le statut du
// plan garde la valeur posee a l'envoi. Voir le commentaire pres du bloc
// d'actions, en bas du rendu.

export default function AnalysisPlanCard({ clientId, onRelaunch }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [plan, setPlan] = useState(null);
  const [updating, setUpdating] = useState(false);
  // Le catalogue sert a confronter le plan a la demande MGD reellement
  // envoyee. Charge a part du plan : son echec ne doit pas empecher
  // d'afficher le plan lui-meme.
  const [catalogue, setCatalogue] = useState(null);
  const [alignError, setAlignError] = useState(null);
  // La fiche cliente, pour le bloc de facturation : il lui faut le nom et
  // l adresse, que le plan ne porte pas.
  const [fiche, setFiche] = useState(null);

  useEffect(() => {
    if (!clientId) {
      setLoading(false);
      return;
    }
    let cancelled = false;

    async function loadLatest() {
      setLoading(true);
      setError(null);
      try {
        const { data, error: err } = await supabase
          .from('analysis_plans')
          .select('*')
          .eq('client_id', clientId)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();
        if (cancelled) return;
        if (err) throw new Error(err.message);
        setPlan(data || null);
      } catch (e) {
        if (!cancelled) setError(e?.message || 'Erreur chargement plan');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadLatest();
    return () => { cancelled = true; };
  }, [clientId]);

  useEffect(() => {
    let annule = false;
    supabase
      .from('lab_tests')
      .select('code,lab_code,display_name,cost_anissa_chf,source_lab,specimen_type,tax_chf,fee_model')
      .eq('is_active', true)
      .then(({ data, error: err }) => {
        if (annule) return;
        // Sur echec, `null` et non `[]` : une liste vide ferait dire au
        // panneau « je ne reconnais aucun code », ce qui accuserait la
        // demande alors que c'est le catalogue qui manque.
        setCatalogue(err ? null : (data || []));
      });
    return () => { annule = true; };
  }, []);

  useEffect(() => {
    let annule = false;
    if (!clientId) return undefined;
    supabase
      .from('clients')
      .select('id,pack_type,form')
      .eq('id', clientId)
      .maybeSingle()
      .then(({ data, error: err }) => { if (!annule) setFiche(err ? null : data); });
    return () => { annule = true; };
  }, [clientId]);

  // Le controle a eu lieu : on le note, y compris quand tout concordait.
  // Un echec d'ecriture est silencieux a dessein — la lecture a bien eu
  // lieu et le resultat est affiche ; casser l'ecran pour un horodatage
  // manque serait disproportionne. Au pire le rappel reste affiche, ce qui
  // est l'erreur du bon cote.
  async function surControle(demande) {
    if (!plan?.id) return;
    try {
      const { data } = await supabase
        .from('analysis_plans')
        .update({
          demande_controlee_le: new Date().toISOString(),
          demande_numero: demande?.numeroDemande || null,
        })
        .eq('id', plan.id)
        .select('*')
        .maybeSingle();
      if (data) setPlan(data);
    } catch { /* le rappel restera, c'est le defaut prudent */ }
  }

  // Ortho : aucun document a relire, la declaration d Anissa fait foi.
  // On aligne le plan sur ce qu elle declare AVANT de noter la
  // confirmation : si l alignement echoue, mieux vaut que le rappel
  // reste affiche que de marquer verifie un plan qui ne l est pas.
  async function surConfirmationOrtho(codes, nbBons) {
    if (!plan?.id) return;
    setAlignError(null);
    const prevus = (plan.selected_tests || []).map((t) => t?.code).filter(Boolean);
    const nonOrtho = (plan.selected_tests || [])
      .filter((t) => t?.source_lab !== 'ortho_analytic')
      .map((t) => t?.code).filter(Boolean);
    const voulus = [...new Set([...nonOrtho, ...codes])];
    const identique = voulus.length === prevus.length
      && voulus.every((c) => prevus.includes(c));

    // ⚠ On recalcule AUSSI quand rien n'a change, si le montant a refacturer
    // manque. Les plans anterieurs au 25.08 n'ont pas cette colonne : sans
    // ce rattrapage, confirmer « j'ai coche exactement ca » les laisserait a
    // null, et la facture partirait avec le pack seul, sans les analyses.
    // Cas reel : le plan de Stephanie, prescrit le 24.08.
    const montantManquant = plan.cost_for_client_chf == null;

    let champs = {};
    if (!identique || montantManquant) {
      const resultat = alignerPlan({ codes: voulus, plan, catalogue: catalogue || [] });
      if (!resultat.ok) { setAlignError(messageRefus(resultat)); return; }
      champs = resultat.champs;
    }

    setUpdating(true);
    try {
      const { data, error: err } = await supabase
        .from('analysis_plans')
        .update({
          ...champs,
          bons_confirmes_le: new Date().toISOString(),
          bons_confirmes_nb: Number.isFinite(nbBons) ? nbBons : null,
        })
        .eq('id', plan.id)
        .select('*')
        .maybeSingle();
      if (err) throw new Error(err.message);
      setPlan(data || null);
    } catch (e) {
      setAlignError(e?.message || 'Erreur lors de la confirmation');
    } finally {
      setUpdating(false);
    }
  }

  async function surAlignement(codes) {
    if (!plan) return;
    setAlignError(null);
    const resultat = alignerPlan({ codes, plan, catalogue: catalogue || [] });
    if (!resultat.ok) {
      setAlignError(messageRefus(resultat));
      return;
    }
    setUpdating(true);
    try {
      const { data, error: err } = await supabase
        .from('analysis_plans')
        .update(resultat.champs)
        .eq('id', plan.id)
        .select('*')
        .maybeSingle();
      if (err) throw new Error(err.message);
      setPlan(data || null);
    } catch (e) {
      setAlignError(e?.message || 'Erreur lors de l’alignement');
    } finally {
      setUpdating(false);
    }
  }

  if (loading) {
    return (
      <div style={cardStyle}>
        <div style={{ color: '#888', fontSize: 13 }}>Chargement du plan d'analyses…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ ...cardStyle, borderColor: 'rgba(196,68,68,0.3)' }}>
        <div style={{ color: '#c44', fontSize: 13 }}>
          Erreur chargement plan : {error}
        </div>
      </div>
    );
  }

  if (!plan) {
    return (
      <div style={cardStyle}>
        <div style={{ color: '#888', fontSize: 13 }}>
          Aucun plan d'analyses créé pour cette cliente.
        </div>
        <div style={{ color: '#aaa', fontSize: 11, marginTop: 6 }}>
          → Va à l’étape 8 de l’anamnèse pour préparer une proposition de plan.
        </div>
      </div>
    );
  }

  const tests = Array.isArray(plan.selected_tests) ? plan.selected_tests : [];
  const status = STATUS_LABELS[plan.status] || STATUS_LABELS.draft;
  const dateStr = plan.created_at
    ? new Date(plan.created_at).toLocaleDateString('fr-CH', { day: '2-digit', month: 'short', year: 'numeric' })
    : '';

  return (
    <div style={cardStyle}>
      <div style={headerStyle}>
        <div>
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>
            Plan d'analyses
          </h3>
          <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>
            Créé le {dateStr} · {tests.length} {tests.length > 1 ? 'analyses' : 'analyse'}
          </div>
        </div>
        <div style={{
          fontSize: 11,
          padding: '3px 10px',
          borderRadius: 12,
          background: `${status.color}22`,
          color: status.color,
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '.04em',
        }}>
          {status.label}
        </div>
      </div>

      {tests.length > 0 && (
        <div style={{ marginTop: 12 }}>
          {tests.map((t, i) => {
            // V97.13 Phase C — affichage du statut cliente (kit envoyé / prise de sang faite)
            const clientDone = t.client_status === 'sent_by_client' || t.client_status === 'sample_taken';
            const clientLabel = t.client_status === 'sent_by_client' ? 'Kit envoyé'
              : t.client_status === 'sample_taken' ? 'Prise de sang effectuée'
              : null;
            const clientAt = t.client_status_at
              ? new Date(t.client_status_at).toLocaleDateString('fr-CH', { day: '2-digit', month: 'long' })
              : null;
            return (
              <div key={t.code || i} style={testRowStyle}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 500 }}>
                    {t.name || t.code}
                  </div>
                  {t.reason && (
                    <div style={{ fontSize: 11, color: '#666', marginTop: 2, fontStyle: 'italic' }}>
                      {t.reason}
                    </div>
                  )}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4, flexWrap: 'wrap' }}>
                    {/* V97.141 : le labo etait deja stocke dans selected_tests
                        mais jamais affiche ici — or c'est sur cette carte
                        qu'Anissa travaille quand elle remplit ses fiches de
                        commande, une par labo. L'info manquait au moment
                        exact ou elle sert. */}
                    {t.source_lab && <LabBadge sourceLab={t.source_lab} />}
                    {t.category && (
                      <span style={categoryTagStyle}>{t.category}</span>
                    )}
                    {clientDone ? (
                      <span style={{
                        fontSize: 10,
                        fontWeight: 600,
                        color: '#2d5a3d',
                        background: 'rgba(45,90,61,0.12)',
                        padding: '2px 8px',
                        borderRadius: 999,
                        letterSpacing: '.03em',
                      }}>
                        ✓ {clientLabel}{clientAt && ` · ${clientAt}`}
                      </span>
                    ) : (
                      <span style={{
                        fontSize: 10,
                        fontWeight: 500,
                        color: '#856404',
                        background: 'rgba(184,134,38,0.10)',
                        padding: '2px 8px',
                        borderRadius: 999,
                        letterSpacing: '.03em',
                      }}>
                        En attente cliente
                      </span>
                    )}
                  </div>
                </div>
                <div style={{ fontSize: 12, color: '#666', whiteSpace: 'nowrap', marginLeft: 12 }}>
                  {formatChf(t.cost_anissa_chf)} CHF
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* V97.12.8 : on retire l'affichage de la "marge" (devenu inutile dans
          le modele V97.12 : pack = accompagnement pur, analyses = supplement).
          On affiche juste le supplement cliente : pack price - credit pack.
          Le credit est lu depuis PACK_DEFINITIONS via plan.pack_type. */}
      <div style={summaryRowStyle}>
        <div style={{ fontSize: 11, color: '#888' }}>
          Pack {formatChf(plan.pack_price_chf)} CHF · {plan.selected_tests?.length || 0} analyse{(plan.selected_tests?.length || 0) > 1 ? 's' : ''} ({formatChf(plan.total_cost_anissa_chf)} CHF)
        </div>
        {(() => {
          // 25.08.2026 — on prefere le montant ENREGISTRE au moment de la
          // prescription. Le recalculer a l'affichage etait le defaut de
          // fond : le chiffre existait, n'etait stocke nulle part, et il
          // fallait le refaire de tete des semaines plus tard devant la
          // facture. Les plans anterieurs n'ont pas la colonne, d'ou le
          // repli sur l'ancien calcul.
          const credit = PACK_DEFINITIONS[plan.pack_type]?.includedAnalysisCreditChf || 0;
          const supplement = plan.cost_for_client_chf != null
            ? Number(plan.cost_for_client_chf)
            : Math.max(0, (plan.total_cost_anissa_chf || 0) - credit);
          return (
            <div style={{
              fontSize: 14,
              fontWeight: 600,
              color: '#2d5a3d',
            }}>
              Supplément cliente : {formatChf(supplement)} CHF
            </div>
          );
        })()}
      </div>

      {/* 25.08.2026 — confronter le plan a la demande reellement envoyee.
          Affiche seulement quand le plan touche MGD : Ortho passe par ses
          bons papier, qui n'ont pas de PDF a relire. */}
      {catalogue && (() => {
        const labos = new Set((plan.selected_tests || []).map(t => t?.source_lab));
        const manquants = controlesManquants(plan);
        if (!labos.has('mgd') && !labos.has('ortho_analytic')) return null;
        return (
          <>
            {manquants.length > 0 && (
              <div style={rappelStyle}>
                <strong style={{ display: 'block', marginBottom: 3 }}>
                  {titreRappelControle(manquants)}
                </strong>
                Ce plan est une prévision. Tant que la commande réelle n’est pas
                vérifiée, rien ne garantit qu’il décrit ce qui a vraiment été
                demandé, ni que le supplément cliente soit le bon.
              </div>
            )}

            {plan.demande_controlee_le && (
              <p style={{ fontSize: 11, color: '#2d5a3d', margin: '10px 0 0' }}>
                Demande {plan.demande_numero ? `n° ${plan.demande_numero} ` : ''}contrôlée le{' '}
                {new Date(plan.demande_controlee_le).toLocaleDateString('fr-CH')}.
              </p>
            )}
            {plan.bons_confirmes_le && (
              <p style={{ fontSize: 11, color: '#2d5a3d', margin: '6px 0 0' }}>
                Bons Ortho confirmés le{' '}
                {new Date(plan.bons_confirmes_le).toLocaleDateString('fr-CH')}
                {plan.bons_confirmes_nb ? ` · ${plan.bons_confirmes_nb} bon${plan.bons_confirmes_nb > 1 ? 's' : ''}` : ''}.
              </p>
            )}

            {labos.has('mgd') && (
              <ControleDemandeMgd
                planTests={plan.selected_tests || []}
                catalogue={catalogue}
                onAligner={updating ? undefined : surAlignement}
                onControle={surControle}
              />
            )}
            {labos.has('ortho_analytic') && !plan.bons_confirmes_le && (
              <ConfirmationBonsOrtho
                planTests={plan.selected_tests || []}
                catalogue={catalogue}
                onConfirmer={updating ? undefined : surConfirmationOrtho}
              />
            )}

            {alignError && (
              <p role="alert" style={{ fontSize: 12, color: '#a33b2c', margin: '8px 0 0', lineHeight: 1.5 }}>
                {alignError}
              </p>
            )}
          </>
        );
      })()}

      {/* 25.08.2026 — Anissa soigne, Benoit facture. Il n'a plus de compte
          sur ce SaaS depuis le 08.08, donc ce depot est ce qui l'informe.
          On attend la fiche : sans nom ni adresse, le bloc n'aurait rien a
          dire et clignoterait au chargement. */}
      {fiche && <EnvoyerAFacturer client={fiche} plan={plan} />}

      {plan.notes_anissa && (
        <div style={notesStyle}>
          {/* V97.12.4 : label compliance "non transmises a la cliente" */}
          <div style={{ fontSize: 10, fontWeight: 600, color: '#6b5018', textTransform: 'uppercase', marginBottom: 4, letterSpacing: '.1em' }}>
            Notes internes Anissa — non transmises à la cliente
          </div>
          <div style={{ fontSize: 12, color: '#555' }}>{plan.notes_anissa}</div>
        </div>
      )}

      {/* 25.08.2026 — LE BOUTON DE TRANSITION DE STATUT A ETE RETIRE.
          Il y avait deux machines a etats en parallele pour ce qu'Anissa
          vit comme une seule decision :

            le parcours    anamnese → analyses → attente resultats → ...
            le plan        draft → sent → in_progress → completed

          Sur l'etape Analyses, la carte proposait « Resultats recus » alors
          que le parcours, juste a cote, demandait de valider pour passer EN
          ATTENTE de resultats. Deux boutons verts, deux vocabulaires,
          contradictoires. Le parcours a raison : rien n'est encore recu.

          Le parcours reste seul maitre. Le statut du plan garde la valeur
          posee a l'envoi ; personne ne s'appuie sur `in_progress` ni sur
          `completed` (la route de l'app cliente accepte deja `sent`, et le
          rappel de controle porte sur sent/in_progress). Le badge d'etat
          reste affiche, il informe sans rien piloter.

          Ne reste ici que « refaire la suggestion », qui n'appartient qu'a
          la carte et ne double aucun controle du parcours. */}
      {plan.status === 'draft' && onRelaunch && (
        <div style={actionRowStyle}>
          {/* V97.141 : relancer la suggestion. Une fois un plan cree, l'ecran
              n'offrait plus aucune sortie : impossible de refaire une
              proposition meme sur un simple brouillon.
              Reserve au BROUILLON : a partir de "envoye", la cliente a recu
              ses kits et son plan est un document reel, on ne le rejoue pas
              dans son dos. La condition est portee par le bloc parent. */}
          <button
            type="button"
            onClick={onRelaunch}
            disabled={updating}
            style={{
              ...actionButtonStyle,
              background: 'transparent',
              color: '#2d5a3d',
              border: '1px solid rgba(45,90,61,0.35)',
              opacity: updating ? 0.6 : 1,
              cursor: updating ? 'wait' : 'pointer',
            }}
            title="Relance une proposition d'analyses. Le brouillon actuel ne sera remplacé que si tu valides le nouveau plan."
          >
            Refaire la suggestion
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Styles inline ────────────────────────────────────────────────
const cardStyle = {
  background: 'rgba(45,90,61,0.04)',
  border: '1px solid rgba(45,90,61,0.15)',
  borderRadius: 8,
  padding: 14,
  margin: '12px 0',
};
const headerStyle = {
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: 12,
};
const testRowStyle = {
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  padding: '8px 10px',
  background: 'rgba(255,255,255,0.5)',
  border: '1px solid rgba(0,0,0,0.05)',
  borderRadius: 6,
  marginBottom: 4,
};
const categoryTagStyle = {
  display: 'inline-block',
  fontSize: 9,
  padding: '1px 6px',
  background: 'rgba(45,90,61,0.1)',
  color: '#2d5a3d',
  borderRadius: 4,
  marginTop: 4,
  textTransform: 'uppercase',
  letterSpacing: '.04em',
};
const summaryRowStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginTop: 12,
  paddingTop: 10,
  borderTop: '1px dashed rgba(0,0,0,0.08)',
};
const notesStyle = {
  marginTop: 10,
  padding: 10,
  background: 'rgba(255,193,7,0.08)',
  borderRadius: 6,
  borderLeft: '3px solid #ffc107',
};
const actionRowStyle = {
  marginTop: 12,
  paddingTop: 10,
  borderTop: '1px dashed rgba(0,0,0,0.08)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
};
const actionButtonStyle = {
  fontSize: 12,
  fontWeight: 600,
  padding: '8px 14px',
  background: '#2d5a3d',
  color: '#fff',
  border: 'none',
  borderRadius: 6,
  letterSpacing: '.02em',
};
// 25.08.2026 — le rappel de controle de la demande. Ambre et non rouge :
// ce n'est pas une erreur, c'est une verification qui manque.
const rappelStyle = {
  marginTop: 12,
  padding: '10px 12px',
  background: 'rgba(232,160,64,0.08)',
  borderLeft: '3px solid #c8892c',
  borderRadius: 6,
  fontSize: 12,
  color: '#6b4a12',
  lineHeight: 1.5,
};
